import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { AmplitudeService, ThousandSeparatorPipe } from '@inyova/inyova-shared';
import { PromotionsVmRewards, PromotionVm } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as AppRootActions from '@app/app.actions';
import * as fromAppRoot from '@app/app.reducers';

import { ToastNotificationService } from '@shared/services/toast-notification.service';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-referrals',
  templateUrl: './referrals.component.html',
  styleUrls: ['./referrals.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ReferralsComponent implements OnInit {
  private store = inject(Store);
  private trackingService = inject(TrackingService);
  private toastNotificationService = inject(ToastNotificationService);
  private amplitudeService: AmplitudeService = inject(AmplitudeService);
  private translateService: TranslateService = inject(TranslateService);

  isSafari = false;
  showShareDialog = false;
  inviteText = '';
  referralCode = '';

  readonly promotionVm$: Observable<PromotionVm> = this.store.select(fromAppRoot.selectReferralsPageVm);

  ngOnInit() {
    this.trackingService.trackActivity('Referral Program: Page visited', true);
    this.store.dispatch(AppRootActions.referralsPageOpened());
    this.isSafari = this.checkIsSafari();
  }

  onCopyToClipboard({ source, content }: { source: 'link' | 'code'; content: string }) {
    this.trackingService.trackActivity(`Referral Program: copy ${source}`, true);
    this.amplitudeService.trackEvent('Referral Shared', { sharing_medium: 'copy_to_clipboard' });

    this.toastNotificationService.show(
      'info',
      this.translateService.instant(`${source === 'code' ? 'tell_friend.copySuccess' : 'tell_friend.copyLinkSuccess'}`)
    );

    const secret = document.createElement('textarea');
    secret.style.position = 'fixed';
    secret.style.left = '0';
    secret.style.top = '0';
    secret.style.opacity = '0';
    secret.value = content;

    document.body.appendChild(secret);

    secret.focus();
    secret.select();

    if (this.isSafari || navigator.userAgent.match(/iPad|Android|Tablet/i)) {
      this.copyClipboardSafari(secret);
    } else {
      this.copyClipboardNonSafari(secret);
    }

    document.body.removeChild(secret);
  }

  onInviteFriends({ promo, currency, referralCode }: { promo: PromotionsVmRewards; currency: string; referralCode: string }) {
    this.showShareDialog = true;
    this.referralCode = referralCode;

    const incentive: { type: '' | 'money' | 'months'; amount: string } = { type: '', amount: '' };

    if (promo.referralMoneyMonthsDuration) {
      incentive.type = 'money';
      incentive.amount = new ThousandSeparatorPipe(this.translateService).transform(
        promo.referralMoneyMoneyMonthly * promo.referralMoneyMonthsDuration,
        currency
      );
    } else {
      incentive.type = 'months';
      incentive.amount = promo.referralMonths.toString();
    }

    this.translateService
      .get(`ACCOUNT.promotion.invite.share.incentive.${incentive.type}`, { code: referralCode, amount: incentive.amount })
      .subscribe((res) => {
        this.inviteText = res;
      });
  }

  onReferralShare(platform: string) {
    this.trackingService.trackActivity(`Referral Program: ${platform}`, true);
    this.amplitudeService.trackEvent('Referral Shared', { sharing_medium: platform });

    if (platform === 'email') {
      location.href =
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        'mailto:?subject=' + (this.translateService.instant('tell_friend.copyMessage.emailTitle') + '&body=' + this.inviteText);
    }
  }

  showPromotionsModal() {
    this.store.dispatch(AppRootActions.showPromotionsDialog({ show: true }));
  }

  onCloseShare() {
    this.showShareDialog = false;
  }

  checkIsSafari() {
    const userAgent = navigator.userAgent;

    return /Safari/i.test(userAgent) && !/Chrome|Chromium|Android/i.test(userAgent);
  }

  copyClipboardSafari(el: any): void {
    const range = document.createRange();
    range.selectNodeContents(el);

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    el.setSelectionRange(0, 999999);

    document.execCommand('copy');

    window.getSelection().removeAllRanges(); // remove all ranges leaving focus and select null
  }

  copyClipboardNonSafari(el): void {
    el.focus();
    el.select(); // select text inside
    document.execCommand('copy'); // copy to clipboard
  }
}
